import React, { useState } from 'react';
import { SnapDialog, Button } from '@one-snap/sparkles';
import PropTypes from 'prop-types';
import axios from 'axios';
import './LtoCheckoutStyles.css';
import AwarenessPage from './AwarenessPage';
import MobilePage from './MobilePage';
import VerificationPage from './VerificationPage';
import PersonalInfoPage from './PersonalInfoPage';
import IncomePage from './IncomePage';
import BankInfoPage from './BankInfoPage';
import getDefaultCredentials from '../../config/defaultCredentials';
import LtoTermsPage from './LtoTermsPage';
import PaymentPlanPage from './PaymentPlanPage';
import AgreementPage from './AgreementPage';

function LtoCheckout({
  isModalVisible,
  handleCancel,
  cart,
  salesTax,
  shipping,
  discount,
  env,
  token,
  openSnackbar
}) {
  const [isAwarenessPageVisible, setIsAwarenessPageVisible] = useState(false);
  const [isMobilePageVisible, setIsMobilePageVisible] = useState(false);
  const [isVerificationPageVisible, setIsVerificationPageVisible] =
    useState(false);
  const [isAboutYouPageVisible, setIsAboutYouPageVisible] = useState(false);
  const [isIncomePageVisible, setIsIncomePageVisible] = useState(false);
  const [isAgreementPageVisible, setIsAgreementPageVisible] = useState(false);
  const [isBankInfoPageVisible, setIsBankInfoPageVisible] = useState(false);
  const [isTermsPageVisible, setIsTermsPageVisible] = useState(false);
  const [isPaymentPlanPageVisible, setIsPaymentPlanPageVisible] =
    useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDialogVisible, setIsDialogVisible] = useState(isModalVisible);
  const [isCreateAppLoading, setCreateAppLoading] = useState(false);
  const [isTermsLoading, setTermsLoading] = useState(false);
  const [isAgreementLoading, setAgreementLoading] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [paymentPlanData, setPaymentPlanData] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [agreementSubmitLoading, setAgreementSubmitLoading] = useState(false);

  if (!env || !token || !cart) return null;

  const calculateTotal = () => {
    const subTotal = cart.reduce(
      (total, item) =>
        total +
        parseFloat(item.description.replace(/[^0-9.-]+/g, '')) * item.quantity,
      0
    );
    return (subTotal + salesTax + shipping - discount).toFixed(2);
  };

  const calculateSubTotal = () =>
    cart.reduce(
      (total, item) =>
        total + item.quantity * parseFloat(item.description.slice(1)),
      0
    );

  const handleSnapClick = () => {
    setIsAwarenessPageVisible(true);
    setIsDialogVisible(false);
  };

  const handleAwarenessPageContinue = () => {
    setIsAwarenessPageVisible(false);
    setIsMobilePageVisible(true);
  };

  const handleAwarenessPageBack = () => {
    setIsDialogVisible(true);
    setIsAwarenessPageVisible(false);
  };

  const handleAwarenessPageClose = () => {
    setIsAwarenessPageVisible(false);
    setIsDialogVisible(true);
  };

  const handleMobileContinue = (enteredPhoneNumber) => {
    setPhoneNumber(enteredPhoneNumber);
    setIsMobilePageVisible(false);
    setIsVerificationPageVisible(true);
  };

  const handleMobilePageBack = () => {
    setIsMobilePageVisible(false);
    setIsAwarenessPageVisible(true);
  };

  const handleMobilePageClose = () => {
    setIsMobilePageVisible(false);
    setIsDialogVisible(true);
  };

  const handleVerificationPageContinue = () => {
    setIsVerificationPageVisible(false);
    setIsAboutYouPageVisible(true);
  };

  const handleVerificationPageBack = () => {
    setIsVerificationPageVisible(false);
    setIsMobilePageVisible(true);
  };

  const handleVerificationPageClose = () => {
    setIsVerificationPageVisible(false);
    setIsDialogVisible(true);
  };

  const handlePersonalInfoPageContinue = () => {
    setIsAboutYouPageVisible(false);
    setIsIncomePageVisible(true);
  };

  const handlePersonalInfoPageBack = () => {
    setIsVerificationPageVisible(true);
    setIsAboutYouPageVisible(false);
  };

  const handlePersonalInfoPageClose = () => {
    setIsAboutYouPageVisible(false);
    setIsDialogVisible(true);
  };

  const handleIncomePageContinue = () => {
    setIsIncomePageVisible(false);
    setIsBankInfoPageVisible(true);
  };

  const handleIncomePageBack = () => {
    setIsIncomePageVisible(false);
    setIsAboutYouPageVisible(true);
  };

  const handleIncomePageClose = () => {
    setIsIncomePageVisible(false);
    setIsDialogVisible(true);
  };

  const handleBankInfoPageBack = () => {
    setIsBankInfoPageVisible(false);
    setIsIncomePageVisible(true);
  };

  const handleBankInfoPageClose = () => {
    setIsBankInfoPageVisible(false);
    setIsDialogVisible(true);
  };

  const handleBankInfoPageContinue = async () => {
    setCreateAppLoading(true);
    const headers = {
      'Content-Type': 'application/json',
      'Referrer-Policy': 'no-referrer-when-downgrade',
      Authorization: `Bearer ${token}`
    };

    const reqBody = {
      customerInformation: {
        dobDate: '1977-07-07',
        customerId: '976908619',
        customerIdType: env === 'sandbox' ? 'SSN4' : 'SSN',
        mobilePhone: '1234567890',
        mobilePhoneCountry: 'US',
        email: 'test@snapfinance.net',
        firstName: 'John',
        lastName: 'Doe',
        billingAddress: {
          streetAddress: '123 Main St. San Jose, CA 95128',
          city: 'San Jose',
          state: 'CA',
          country: 'US',
          postalCode: '95128'
        },
        driversLicenseNumber: '123456879',
        driversLicenseState: 'CA',
        language: 'EN',
        phoneOptIn: true
      },
      cartInformation: {
        currencyCode: 'USD',
        taxAmount: salesTax.toFixed(2),
        shippingAmount: shipping.toFixed(2),
        totalAmount: calculateTotal(),
        discountAmount: discount.toFixed(2),
        orderId: 'test001',
        items: [
          {
            price: calculateSubTotal().toFixed(2),
            itemId: 'test001',
            description: 'test001',
            sku: 'test001',
            quantity: 1,
            leasable: true
          }
        ],
        shippingAddress: {
          streetAddress: '15063 E 14th St',
          city: 'San Leandro',
          postalCode: '90201',
          state: 'CA',
          country: 'US',
          unit: '1'
        }
      },
      bankInformation: {
        bankName: 'Bank of America',
        bankCity: 'San Jose',
        bankState: 'CA',
        routingNumber: '021000021',
        accountNumber: '765765765',
        directDeposit: true
      },
      incomeInformation: [
        {
          employerName: 'AcmeCorporation',
          frequency: 'BI_WEEKLY',
          incomeAmount: '5000.00',
          phone: '1234567890',
          phoneCountryCode: 'US',
          positionHeld: 'Associate',
          type: 'FULL_TIME',
          lastPayDate: '2020-02-07',
          nextPayDate: '2020-02-21'
        }
      ]
    };

    const response = await axios.post(
      `${getDefaultCredentials(env).apiUrl}/application`,
      reqBody,
      { headers }
    );
    setApplicationId(response.data.data.applicationId);
    setCreateAppLoading(false);
    setIsBankInfoPageVisible(false);
    setIsTermsPageVisible(true);
  };

  const handleTermsPageClose = () => {
    setIsTermsPageVisible(false);
    setIsDialogVisible(true);
  };

  const handleTermsPageBack = () => {
    setIsTermsPageVisible(false);
    setIsBankInfoPageVisible(true);
  };

  const handleAgreementPageClose = () => {
    setIsAgreementPageVisible(false);
    setIsDialogVisible(true);
  };

  const handleTermsPageContinue = async () => {
    setTermsLoading(true);
    const headers = {
      'Content-Type': 'application/json',
      'Referrer-Policy': 'no-referrer-when-downgrade',
      Authorization: `Bearer ${token}`
    };
    const currentDate = new Date();

    const termsAndConditions = {
      customerInformation: {
        phoneOptIn: true
      },
      termsConditions: {
        acceptance: true
      },
      complianceDisclosures: {
        termsConditions: {
          version: '0',
          timestamp: currentDate.toISOString()
        },
        electronicTransaction: {
          version: '0',
          timestamp: currentDate.toISOString()
        },
        snapAuthorization: {
          version: '0',
          timestamp: currentDate.toISOString()
        }
      }
    };

    await axios.patch(
      getDefaultCredentials(env).apiUrl + `/application/${applicationId}`,
      termsAndConditions,
      { headers }
    );

    const paymentPlanResponse = await axios.get(
      `${getDefaultCredentials(env).apiUrl}/application/${
        applicationId
      }/paymentPlan`,
      {
        headers: {
          ...headers
        }
      }
    );
    setPaymentPlanData(paymentPlanResponse.data);
    setTermsLoading(false);
    setIsTermsPageVisible(false);
    setIsPaymentPlanPageVisible(true);
  };

  const handlePaymentPlanPageBack = () => {
    setIsPaymentPlanPageVisible(false);
    setIsTermsPageVisible(true);
  };

  const handlePaymentPlanPageClose = () => {
    setIsPaymentPlanPageVisible(false);
    setIsDialogVisible(true);
  };

  const handlePaymentPlanPageContinue = async () => {
    setAgreementLoading(true);
    const headers = {
      'Content-Type': 'application/json',
      'Referrer-Policy': 'no-referrer-when-downgrade',
      Authorization: `Bearer ${token}`
    };

    const agreementResponse = await axios.get(
      `${getDefaultCredentials(env).apiUrl}/application/${
        applicationId
      }/agreement?svgonly=true&language=EN&summaryAcceptance=true`,
      {
        headers: {
          ...headers
        }
      }
    );
    setAgreementData(agreementResponse.data);
    setAgreementLoading(false);
    setIsPaymentPlanPageVisible(false);
    setIsAgreementPageVisible(true);
  };

  const handleAgreementPageBack = () => {
    setIsAgreementPageVisible(false);
    setIsPaymentPlanPageVisible(true);
  };

  const handleAgreementPageContinue = async () => {
    setAgreementSubmitLoading(true);
    const signatureData = {
      agreementInformation: {
        signedTime: new Date().toISOString().slice(0, 19) + 'Z',
        signedMethod: 'acceptance',
        signedBy: 'John Doe',
        device: 'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36',
        deviceIP: '10.1.1.10'
      }
    };
    const headers = {
      'Content-Type': 'application/json',
      'Referrer-Policy': 'no-referrer-when-downgrade',
      Authorization: `Bearer ${token}`
    };

    await axios.post(
      getDefaultCredentials(env).apiUrl +
        `/application/${applicationId}/signed`,
      signatureData,
      { headers }
    );
    setAgreementSubmitLoading(false);
    setIsAgreementPageVisible(false);
    openSnackbar();
    handleCancel();
  };

  const totalAmount = calculateTotal();

  return (
    <div>
      {isDialogVisible && (
        <SnapDialog
          data-testid="checkout-partner-modal"
          open={isDialogVisible}
          onClose={handleCancel}
        >
          <div className="store-modal">
            <div className="store-modal-title">Are these details correct?</div>
            <div className="store-modal-subtitle">
              Review below and go back to make changes if needed.
            </div>
            <div className="shopping-cart-card">
              <div className="store-address-panel">
                <div>
                  <b>Shipping Address</b>
                </div>
                <div>John Doe</div>
                <div className="store-add-item">
                  123 Main St. San Jose, CA 95128
                </div>
              </div>
              <div className="store-address-panel">
                <div>
                  <b>Products</b>
                </div>
                {cart.length === 0 ? (
                  <div>No items in the cart.</div>
                ) : (
                  cart.map((item, index) => (
                    <div
                      key={item.id}
                      className="store-cart-item"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div>
                        {index + 1}. {item.fancyName}
                      </div>
                      <div>
                        {item.quantity} x $
                        {parseFloat(
                          item.description.replace(/[^0-9.-]+/g, '')
                        ).toFixed(2)}
                      </div>
                    </div>
                  ))
                )}
              </div>
              {cart.length > 0 && (
                <div
                  className="store-cart-subtotal"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: '10px',
                    fontWeight: 'bold'
                  }}
                >
                  <span />
                  <span>
                    Sub-Total&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;$
                    {cart
                      .reduce(
                        (total, item) =>
                          total +
                          parseFloat(
                            item.description.replace(/[^0-9.-]+/g, '')
                          ) *
                            item.quantity,
                        0
                      )
                      .toFixed(2)}
                  </span>
                </div>
              )}
            </div>
            <div
              className="sale-numbers"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>
                <b>Sales Tax</b>
              </span>
              <span>${salesTax.toFixed(2)}</span>
            </div>
            <div
              className="sale-numbers"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>Shipping</span>
              <span>${shipping.toFixed(2)}</span>
            </div>
            <div
              className="sale-numbers"
              style={{ display: 'flex', justifyContent: 'space-between' }}
            >
              <span>Discount</span>
              <span>${discount.toFixed(2)}</span>
            </div>
            <div
              className="sale-numbers sale-numbers-total"
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontWeight: 'bold'
              }}
            >
              <span>
                <b>Total Amount</b>
              </span>
              <span>${calculateTotal()}</span>
            </div>
            <div className="lease-to-own-snap">
              <div style={{ marginBottom: '10px', marginRight: '80px' }}>
                <img src="/lto-image.svg" alt="lease-to-own" />
              </div>
            </div>
            <div>
              <Button className="lto-checkout-btn" onClick={handleSnapClick}>
                <img
                  src="/snap-lto-icon.svg"
                  alt="snap icon"
                  className="snap-ico"
                />
                <div className="pay-with-snap-div">Pay with Snap</div>
              </Button>
            </div>
          </div>
        </SnapDialog>
      )}

      {isAwarenessPageVisible && (
        <AwarenessPage
          isVisible={isAwarenessPageVisible}
          onClose={handleAwarenessPageClose}
          totalAmount={totalAmount}
          goBack={handleAwarenessPageBack}
          onContinue={handleAwarenessPageContinue}
        />
      )}

      {isMobilePageVisible && (
        <MobilePage
          isVisible={isMobilePageVisible}
          onClose={handleMobilePageClose}
          totalAmount={totalAmount}
          onContinue={handleMobileContinue}
          goBack={handleMobilePageBack}
        />
      )}

      {isVerificationPageVisible && (
        <VerificationPage
          isVisible={isVerificationPageVisible}
          onClose={handleVerificationPageClose}
          totalAmount={totalAmount}
          phoneNumber={phoneNumber}
          onContinue={handleVerificationPageContinue}
          goBack={handleVerificationPageBack}
        />
      )}

      {isAboutYouPageVisible && (
        <PersonalInfoPage
          isVisible={isAboutYouPageVisible}
          onClose={handlePersonalInfoPageClose}
          totalAmount={totalAmount}
          phoneNumber={phoneNumber}
          onContinue={handlePersonalInfoPageContinue}
          goBack={handlePersonalInfoPageBack}
        />
      )}

      {isIncomePageVisible && (
        <IncomePage
          isVisible={isIncomePageVisible}
          onClose={handleIncomePageClose}
          totalAmount={totalAmount}
          goBack={handleIncomePageBack}
          onContinue={handleIncomePageContinue}
        />
      )}
      {isBankInfoPageVisible && (
        <BankInfoPage
          isVisible={isBankInfoPageVisible}
          onClose={handleBankInfoPageClose}
          totalAmount={totalAmount}
          goBack={handleBankInfoPageBack}
          onContinue={handleBankInfoPageContinue}
          isSubmitLoading={isCreateAppLoading}
        />
      )}
      {isTermsPageVisible && (
        <LtoTermsPage
          isVisible={isTermsPageVisible}
          onClose={handleTermsPageClose}
          totalAmount={totalAmount}
          goBack={handleTermsPageBack}
          onContinue={handleTermsPageContinue}
          isSubmitLoading={isTermsLoading}
        />
      )}
      {isPaymentPlanPageVisible && (
        <PaymentPlanPage
          isVisible={isPaymentPlanPageVisible}
          totalAmount={totalAmount}
          applicationId={applicationId}
          paymentPlan={paymentPlanData}
          onClose={handlePaymentPlanPageClose}
          goBack={handlePaymentPlanPageBack}
          isSubmitLoading={isAgreementLoading}
          onContinue={handlePaymentPlanPageContinue}
        />
      )}
      {isAgreementPageVisible && (
        <AgreementPage
          isVisible={isAgreementPageVisible}
          totalAmount={totalAmount}
          agreementData={agreementData}
          onClose={handleAgreementPageClose}
          goBack={handleAgreementPageBack}
          onContinue={handleAgreementPageContinue}
          loading={agreementSubmitLoading}
        />
      )}
    </div>
  );
}

LtoCheckout.propTypes = {
  isModalVisible: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  cart: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      fancyName: PropTypes.string.isRequired,
      quantity: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired
    })
  ).isRequired,
  salesTax: PropTypes.number.isRequired,
  shipping: PropTypes.number.isRequired,
  discount: PropTypes.number.isRequired,
  env: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  openSnackbar: PropTypes.func.isRequired
};

export default LtoCheckout;
